import {
  Button,
  Input,
  InputNumber,
  message,
  Modal,
  Slider,
  Upload,
} from 'antd';
import { useContext, useState } from 'react';
import styled from 'styled-components';
import { createPortfolio } from '../../../api/copyTrading';
import API from '@/utils/api';
import { VITE_APP_API_MEDIA_DEV_URL } from '@/app/constants/APIKEYS';
import { useToken } from '@/app/hooks/auth/token';

import { media } from '@/styles/Qtheme.ts';
import { ThemeContext } from '@/theme/ThemeProvider';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DeskTopCalVW,
  MobileCalVW,
  TabletCalVW,
  useIsTablet,
} from '@utils/CalVW.tsx';
import { useTranslation } from 'react-i18next';

const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};
const beforeUpload = (file) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message.error('You can only upload JPG/PNG file!');
  }
  const isLt10M = file.size / 1024 / 1024 <= 10;
  if (!isLt10M) {
    message.error('Image must be 10MB or smaller!');
  }
  return isJpgOrPng && isLt10M;
};

export const CopyRegisterModal = ({ setOpen, open }: any) => {
  const { t } = useTranslation();
  const { data: token } = useToken();

  // Form State
  const [imageUrl, setImageUrl] = useState<string>(); // 미리보기용 (Base64)
  const [imageKey, setImageKey] = useState<string>(); // 실제 저장 키
  const [portfolioName, setPortfolioName] = useState('');
  const [description, setDescription] = useState('');
  const [profitShareRate, setProfitShareRate] = useState(15); // 5-30%
  const [minCopyAmount, setMinCopyAmount] = useState(100);

  // 유틸리티
  const isTablet = useIsTablet();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  // 이미지 업로드 상태
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = async (info) => {
    if (loading) return;
    setLoading(true);

    try {
      // 1. Presigned URL 요청
      const response = await API.post('/upload/presigned-url', token, {
        usage: 'copy-portfolio-image',
        fileExtension: info?.file?.type === 'image/jpeg' ? 'jpg' : 'png',
      });

      if (response.success) {
        // 2. R2에 직접 업로드
        const fileUrl = response.result.url;
        const uploadRes = await fetch(fileUrl, {
          method: 'PUT',
          body: info?.file?.originFileObj,
          headers: { 'Content-Type': info?.file?.type },
        });

        if (uploadRes.ok) {
          // 3. 업로드 성공 확인
          const fullImageUrl = `${VITE_APP_API_MEDIA_DEV_URL}/${response.result.key}`;
          const checkRes = await fetch(fullImageUrl);

          if (checkRes.ok) {
            const key = response.result.key;
            console.log('[Image Upload] Success - Key:', key);
            setImageKey(key); // 실제 저장용
            getBase64(info.file.originFileObj, (url) => {
              setImageUrl(url); // 미리보기용
              console.log('[Image Upload] Preview URL set');
            });
            message.success(
              t('imageUploadSuccess') || 'Image uploaded successfully'
            );
          } else {
            console.error('[Image Upload] Check failed for URL:', fullImageUrl);
            message.error(
              t('imageUploadFailed') ||
                'Uploaded image is not yet available. Please try again later.'
            );
          }
        }
      }
    } catch (error) {
      console.error('Upload failed', error);
      message.error(t('imageUploadFailed') || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const uploadButton = (
    <UploadButton type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        style={{
          marginTop: 8,
        }}
      >
        {t('uploadImage')}
      </div>
    </UploadButton>
  );

  const handleCancel = () => {
    setOpen(false);
    // 폼 초기화
    setImageUrl(undefined);
    setImageKey(undefined);
    setPortfolioName('');
    setDescription('');
    setProfitShareRate(15);
    setMinCopyAmount(100);
  };

  const handleOk = async () => {
    // 입력값 검증
    if (!portfolioName.trim()) {
      message.error(
        t('pleaseEnterPortfolioName') || 'Please enter portfolio name'
      );
      return;
    }

    if (portfolioName.length < 3) {
      message.error('Portfolio name must be at least 3 characters');
      return;
    }

    if (!imageKey) {
      message.warning(t('pleaseUploadImage'));
      console.warn('[Portfolio Create] No imageKey - upload required');
      return;
    }

    console.log('[Portfolio Create] Submitting with imageKey:', imageKey);

    setSubmitting(true);

    try {
      // API 호출
      const requestData = {
        portfolioName: portfolioName,
        description: description || undefined,
        imageUrl: imageKey, // R2 key (예: "copy-portfolios/abc123.jpg")
        profitShareRate: profitShareRate,
        minCopyAmount: minCopyAmount,
      };

      console.log('[Portfolio Create] Request data:', requestData);

      const response = await createPortfolio(requestData);

      if (response.success) {
        message.success(
          t('portfolioCreatedSuccessfully') || 'Portfolio created successfully!'
        );
        setOpen(false);
        handleCancel(); // 폼 초기화

        // 페이지 새로고침 또는 Store 갱신 필요
        window.location.reload();
      } else {
        message.error(response.error?.message || 'Failed to create portfolio');
      }
    } catch (error: any) {
      console.error('Create portfolio error:', error);
      message.error(
        error.response?.data?.error?.message || 'Failed to create portfolio'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      width={isTablet ? '90%' : `500px`}
      centered={true}
      closable={true}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {t('cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleOk}
        >
          {t('leaderRegistration')}
        </Button>,
      ]}
      title={t('becomeALeader')}
      className={'p2pmodal' + (theme === 'dark' ? ' dark' : '')}
    >
      <ModalContents theme={theme}>
        <TabletWrapper style={{ width: '100%' }}>
          <LabelWrap>
            <Label>{t('portfolioImage')}</Label>
            <Upload
              name="avatar"
              listType="picture-circle"
              className="avatar-uploader"
              showUploadList={false}
              beforeUpload={beforeUpload}
              onChange={handleChange}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="avatar"
                  style={{
                    width: '100%',
                  }}
                />
              ) : (
                uploadButton
              )}
            </Upload>
          </LabelWrap>

          <LabelWrap>
            <Label>{t('portfolioName')}</Label>
            <StyledInput
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              placeholder={t('portfolioName')}
            />
          </LabelWrap>

          <LabelWrap>
            <Label>{t('portfolioDescription')}</Label>
            <StyledInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('portfolioDescription')}
            />
          </LabelWrap>

          <LabelWrap>
            <Label>{t('profitShareRate')}</Label>
            <div
              style={{
                width: '300px',
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              <Slider
                min={5}
                max={30}
                value={profitShareRate}
                onChange={setProfitShareRate}
                style={{ flex: 1 }}
              />
              <InputNumber
                min={5}
                max={30}
                value={profitShareRate}
                onChange={(val) => setProfitShareRate(val || 15)}
                formatter={(value) => `${value}%`}
                parser={(value) => value?.replace('%', '') as any}
                style={{ width: '80px' }}
              />
            </div>
          </LabelWrap>

          <LabelWrap>
            <Label>{t('minCopyAmount')}</Label>
            <InputNumber
              min={100}
              max={100000}
              value={minCopyAmount}
              onChange={(val) => setMinCopyAmount(val || 100)}
              formatter={(value) => `${value} USDT`}
              parser={(value) => value?.replace(' USDT', '') as any}
              style={{ width: '300px' }}
            />
          </LabelWrap>
        </TabletWrapper>
      </ModalContents>

      {/*<CModal*/}
      {/*  visible={showCurrencyModal}*/}
      {/*  onClose={closeCurrencyModal}*/}
      {/*  bgColor={'transparent'}>*/}
      {/*  <P2PCurrencyModal closeModal={closeCurrencyModal} setLang={setLang} lang={lang}/>*/}
      {/*</CModal>*/}
    </Modal>
  );
};

const UploadButton = styled.button`
  border: 0;
  background: none;
  color: ${({ theme }) => theme.text};
`;

const LabelWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.div`
  width: 100px;
`;

const StyledInput = styled(Input)`
  width: 300px;
  height: 40px;
  border: ${({ theme }) => `1.5px solid ${theme.searchBg}`};
  border-radius: 12.08px;
  font-size: 16px;
  font-weight: 700;
  line-height: 18.4px;
  background-color: ${({ theme }) => theme.body} !important;
  padding-left: 10px;
  font-family: Arial sans-serif;
  color: ${({ theme }) => theme.text} !important;

  &::placeholder {
    color: ${({ theme }) => theme.text} !important;
  }

  // @media ${media.desktop2} {
  //   height: ${DeskTopCalVW(65)};
  //   border-radius: ${DeskTopCalVW(12.08)};
  //   line-height: ${DeskTopCalVW(18.4)};
  //   padding-left: ${DeskTopCalVW(23)};
  //   margin-bottom: ${DeskTopCalVW(31)};
  // }

  @media ${media.desktop} {
    height: ${TabletCalVW(45)};
    border-radius: ${TabletCalVW(12.08)};
    line-height: ${TabletCalVW(18.4)};
    padding-left: ${TabletCalVW(23)};
    margin-bottom: ${TabletCalVW(31)};
  }

  @media ${media.mobile} {
    height: ${MobileCalVW(30)};
    border-radius: ${MobileCalVW(5.08)};
    line-height: ${MobileCalVW(18.4)};
    padding-left: ${MobileCalVW(13)};
    margin-bottom: ${MobileCalVW(21)};
    font-size: ${MobileCalVW(12)};
    line-height: ${MobileCalVW(14.4)};
  }
`;

const ModalContents = styled.div<{ theme?: string }>`
  display: flex;
  background-color: ${({ theme }) => (theme === 'dark' ? '#1a1a1a' : '#fff')};
  border-radius: 10;

  @media ${media.desktop} {
    height: 550px;
    overflow-y: scroll;
  }

  @media ${media.mobile} {
    height: ${MobileCalVW(320)};
    overflow-y: scroll;
  }
`;

const TabletWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: ${({ theme }) => theme.itemBg};
  padding: 30px 0;
  gap: 20px;

  @media ${media.desktop} {
    flex-direction: column;
    justify-content: flex-start;
  }
`;
